/* eslint-disable @typescript-eslint/no-explicit-any */
import { BlurFilter, Graphics, Text } from "pixi.js";
import DisplayContainerOptions from "../interfaces/DisplayContainerOptions";
import * as sdk from "microsoft-cognitiveservices-speech-sdk";
import Fa from "../utils/Fa";
import AppModel from "../models/AppModel";
import { AssessmentPart, AssessmentQuestion } from "../models/AssessmentData";
import ActivityContainer from "../components/ActivityContainer";
import IconButton from "../components/IconButton";
import { SpeechPhonemeResult, SpeechResult } from "../types/SpeechResult";
import SoundManager from "../managers/SoundManager";

export default class SpeechRecog extends ActivityContainer {

    private bg: Graphics;
    private model: AppModel;
    private soundManager: SoundManager;

    private data: AssessmentPart;
    private questions: AssessmentQuestion[];
    private incorrectCount: number;
    private discontinuationCount: number;
    private currentQuestion: AssessmentQuestion;
    private recording: boolean;
    private thinking: boolean;
    private stream: any;
    private recordBtn: IconButton;
    private assessmentText: Text;
    private assessmentString: string;
    private blurFilter: BlurFilter;
    private result: SpeechResult;
    private playAudio: boolean;


    private speechConfig: sdk.SpeechConfig;
    private audioConfig: sdk.AudioConfig;
    private language: string;
    private speechRecognizer: sdk.SpeechRecognizer;
    private allWords: any[];
    private mediaRecorder: MediaRecorder | null = null;
    private audioChunks: Blob[] = [];
    private audioUrl: string;
    private audioBlob: Blob;
    private audioJsonFromAzure: any[];
    private recognizedSomething: boolean;

    constructor(/*readyFunction: Function, */part: AssessmentPart, completeFunction: Function, playAudio: boolean, options?: DisplayContainerOptions) {
        super(completeFunction);
        //this.readyFunction = readyFunction;
        this.data = part;
        this.playAudio = playAudio;
        if (options) {
            this.options = options;
        }
        else {
            this.setDefaultOptions();
        }
        this._init();
    }

    private _init() {
        this.label = "Menu";
        this.model = AppModel.getInstance();
        this.soundManager = SoundManager.getInstance();
        this.recording = false;
        this.thinking = false;
        this.stream = null;
        this.discontinuationCount = this.data.discontinuationCount;
        this.language = "en-US";
        this.blurFilter = new BlurFilter();
        this.blurFilter.strength = 15;
        this.setupQuestions();
        if (this.options.autoCreate) {
            this.create(this.options.showAfterCreate);
        }
    }

    private createRecordBtn = (): void => {
        this.recordBtn = new IconButton(Fa.solid.microphone, "fontawesome6prosolid");
        this.recordBtn.label = "startBtn";
        this.recordBtn.bgColor = "#a9d3ef";// #9550B4";
        this.recordBtn.iconColor = /*"#000000";//*/ "#FFFFFF";
        this.recordBtn.strokeColor = "#93c8eb";// "#FFFFFF";
        this.recordBtn.onpointerdown = this.handleClick;
        this.recordBtn.create();
        this.recordBtn.x = this.model.centerX - this.recordBtn.width / 2;
        this.recordBtn.y = 560;
        this.recordBtn.disable();
        this.addChild(this.recordBtn);
    }

    private createText(): void {
        this.assessmentText = new Text({
            label: "Assessment Text",
            text: this.currentQuestion.question,
            style: {
                fontSize: 62,
                fill: "#061054",
                align: "center"
            },
        });
        this.assessmentText.x = this.model.centerX - this.assessmentText.width / 2;
        this.assessmentText.y = this.model.centerY - this.assessmentText.height / 2;
        this.assessmentText.filters = [this.blurFilter];
        this.addChild(this.assessmentText);
        if (this.playAudio) {
            this.soundManager.playSound(`/assets/audio/157.mp3`).then(() => { this.recordBtn.enable(); });
            this.playAudio = false;
        } else {
            this.recordBtn.enable();
        }
    }

    private getMedia = async (constraints): Promise<boolean> => {
        try {
            this.stream = await navigator.mediaDevices.getUserMedia(constraints);
            return true;
        } catch (err) {
            console.error('Could not access microphone:', err);
            alert('Microphone access was denied. Please enable it in your browser settings and try again.');
            return false;
        }
    }

    private handleClick = (): void => {
        if (!this.thinking) {
            if (this.recording === false) {
                this.startRecording();
                this.recordBtn.swapIcon(Fa.solid.stop);
            } else {
                this.thinking = true;
                this.stopRecording();
                this.recordBtn.swapIcon(Fa.solid.spinner);
                this.recordBtn.spinIcon(true);
            }
        }
    }

    private async startRecording() {
        // Stop and clean up existing stream if it exists
        if (this.stream) {
            this.stream.getTracks().forEach((track) => track.stop());
            this.stream = null;
        }

        // Request microphone permissions
        const permissionGranted = await this.getMedia({ audio: true });
        if (!permissionGranted) {
            // Reset button if permissions are denied
            this.recording = false;
            this.thinking = false;
            this.recordBtn.swapIcon(Fa.solid.microphone);
            this.recordBtn.spinIcon(false);
            return;
        }

        // Ensure audio chunks are reset
        this.audioChunks = [];
        this.recognizedSomething = false;
        this.recording = true;
        this.allWords = [];
        this.audioJsonFromAzure = [];

        // local audio recording
        try {
            // Determine the supported MIME type
            let mimeType = "";
            if (MediaRecorder.isTypeSupported("audio/aac")) {
                mimeType = "audio/aac"; // Better compatibility with Safari
            } else if (MediaRecorder.isTypeSupported("audio/mp4")) {
                mimeType = "audio/mp4"; // Fallback for Safari compatibility
            } else if (MediaRecorder.isTypeSupported("audio/webm")) {
                mimeType = "audio/webm"; // Fallback for Chrome and Firefox
            } else {
                throw new Error("No suitable audio format found for recording.");
            }

            // Create a MediaRecorder instance
            this.mediaRecorder = new MediaRecorder(this.stream, { mimeType });

            // Event handler to collect audio data chunks
            this.mediaRecorder.ondataavailable = (event: BlobEvent) => {
                if (event.data.size > 0) {
                    this.audioChunks.push(event.data);
                }
            };

            // Start recording with timeslice for consistent data collection
            this.mediaRecorder.start(1000); // Emit data every 1000ms
            console.log("Recording started with MIME type:", mimeType);
        } catch (error) {
            console.error("Error accessing the microphone", error);
        }

        this.speechConfig = sdk.SpeechConfig.fromSubscription("5b9b44f4d4f84ea184155f03a2b2bbea", "westus2");//rh
        this.audioConfig = sdk.AudioConfig.fromDefaultMicrophoneInput();
        // eslint-disable-next-line no-control-regex
        this.assessmentString = this.currentQuestion.question.replace(new RegExp("[\r\n]+", "g"), " ");
        const pronunciationConfig = new sdk.PronunciationAssessmentConfig(
            this.assessmentString,
            sdk.PronunciationAssessmentGradingSystem.HundredMark,
            sdk.PronunciationAssessmentGranularity.Phoneme,
            false
        );
        this.speechConfig.speechRecognitionLanguage = this.language;
        this.speechRecognizer = new sdk.SpeechRecognizer(this.speechConfig, this.audioConfig);
        pronunciationConfig.applyTo(this.speechRecognizer);

        this.speechRecognizer.recognizing = (s, e) => {
            const str = "(recognizing) Reason: " + sdk.ResultReason[e.result.reason] + " Text: " + e.result.text;
            console.log(str);
        }

        this.speechRecognizer.recognized = (s, e) => {
            if (e.result.reason === sdk.ResultReason.RecognizedSpeech) {
                try {
                    this.audioJsonFromAzure.push(e.result);
                    console.log("pronunciation assessment for: ", e.result.text);
                    const pronunciation_result = sdk.PronunciationAssessmentResult.fromResult(e.result);
                    console.log(" Accuracy score: ", pronunciation_result.accuracyScore, '\n',
                        "pronunciation score: ", pronunciation_result.pronunciationScore, '\n',
                        "completeness score : ", pronunciation_result.completenessScore, '\n',
                        "fluency score: ", pronunciation_result.fluencyScore
                    );

                    const jo = JSON.parse(e.result.properties.getProperty(sdk.PropertyId.SpeechServiceResponse_JsonResult));
                    const isSucceeded = jo.RecognitionStatus === 'Success';
                    const nBestWords = jo.NBest[0].Words;
                    if (isSucceeded && nBestWords) {
                        this.allWords.push(...nBestWords);
                    }
                    this.recognizedSomething = true;
                } catch (error) {
                    console.log('recognized error: ' + error);
                }
            }
        }

        this.speechRecognizer.canceled = (s, e) => {
            console.log("(cancel) Reason: " + sdk.CancellationReason[e.reason]);
            if (e.reason === sdk.CancellationReason.Error) {
                console.log("Error: " + e.errorDetails);
            }
            this.speechRecognizer.stopContinuousRecognitionAsync();
        }

        this.speechRecognizer.sessionStarted = (s, e) => {
            console.log('session started');
            this.assessmentText.filters = null;
        }

        this.speechRecognizer.sessionStopped = (s, e) => {
            console.log('session stopped');
            this.calculateOverallPronunciationScore();
        }

        this.speechRecognizer.startContinuousRecognitionAsync();
    }

    private stopRecording() {
        this.recording = false;

        this.speechRecognizer.stopContinuousRecognitionAsync(
            function () {
                this.speechRecognizer.close()
                this.speechRecognizer = undefined;
            },
            function (err) {
                this.speechRecognizer.close();
                this.speechRecognizer = undefined;
            }
        );

        //local recording
        if (this.mediaRecorder && this.mediaRecorder.state !== "inactive") {
            // Listen for the 'stop' event to ensure all data is captured
            this.mediaRecorder.onstop = async () => {
                console.log("Recording stopped");

                // Stop all tracks in the stream
                if (this.stream) {
                    this.stream.getTracks().forEach((track) => track.stop());
                }

                // Create a blob from the audio chunks
                const mimeType = this.mediaRecorder.mimeType;
                this.audioBlob = new Blob(this.audioChunks, { type: mimeType });
                this.audioChunks = []; // Clear the audio chunks

                // Revoke previous URL if it exists to free up memory
                if (this.audioUrl) {
                    URL.revokeObjectURL(this.audioUrl);
                    this.audioUrl = null; // Clear the reference
                }

                // Create an audio URL for playback
                this.audioUrl = URL.createObjectURL(this.audioBlob);
                console.log("Audio URL created:", this.audioUrl);



                // Dispose of the MediaRecorder instance
                this.mediaRecorder = null; // Properly clean up the recorder instance
            };

            // Stop recording
            this.mediaRecorder.stop();
        }
    }

    private async calculateOverallPronunciationScore() {
        if (this.recognizedSomething == true) {
            try {
                const len = this.allWords.length;
                let best = this.allWords[0];
                for (let i = 1; i < len; i++) {
                    let word = this.allWords[i];
                    if (word.PronunciationAssessment.AccuracyScore > best.PronunciationAssessment.AccuracyScore) {
                        best = word;
                    }
                }
                let result = new SpeechResult();
                result.AccuracyScore = best.PronunciationAssessment.AccuracyScore;
                result.Word = best.Word;
                result.SpeechPhonemeResults = [];
                const phonLen = best.Phonemes.length;
                for (let y = 0; y < phonLen; y++) {
                    let phoneme = best.Phonemes[y];
                    let phonResult = new SpeechPhonemeResult();
                    phonResult.Phoneme = phoneme.Phoneme;
                    phonResult.AccuracyScore = phoneme.PronunciationAssessment.AccuracyScore;
                    result.SpeechPhonemeResults.push(phonResult);
                }

                let formData = new FormData();
                formData.append('userId', this.model.userId.toString());
                formData.append('questionId', this.currentQuestion.id.toString());
                formData.append('result', JSON.stringify(result));
                formData.append('file', new File([this.audioBlob], "audio.mp4", { type: this.audioBlob.type }));
                formData.append('azureJson', JSON.stringify(this.audioJsonFromAzure));
                await fetch(`/Speech/SaveSpeechResult`, {
                    method: 'POST',
                    body: formData
                }).then(response => {
                    if (!response.ok) {
                        throw new Error(`Error saving answer: ${response.status}`)
                    }
                }).catch(e => {
                    console.error(e);
                });

                this.playNext();
            } catch (ex) {
                console.log('error: ' + ex);
            }
        } else {
            this.assessmentText.filters = [this.blurFilter];
            console.log('Nothing was recognized.');
            alert('Nothing was recognized. Please try again.');
        }

        this.thinking = false;
        this.recordBtn.spinIcon(false);
        this.recordBtn.swapIcon(Fa.solid.microphone);
    }

    private playNext(): void {
        this.removeChild(this.assessmentText);
        this.currentQuestion = this.questions.shift();
        if (this.currentQuestion !== undefined) {
            this.createText();
        } else {
            this.endEarly = this.incorrectCount >= this.data.discontinuationCount;
            this.activityComplete();
        }
    }

    private setDefaultOptions(): void {
        this.options = {
            autoCreate: true,
            showAfterCreate: true
        };
    }

    private setupQuestions(): void {
        this.incorrectCount = 0;
        this.questions = [];
        const numQuestions = this.data.questions.length;
        for (let i = 0; i < numQuestions; i++) {
            const curQuestion = this.data.questions[i];
            if (curQuestion.isComplete === false) {
                this.questions.push(curQuestion);
            } else if (curQuestion.correct === false) {
                this.incorrectCount++;
            }
        }
    }

    //----------------------------------
    //  Public:
    //----------------------------------

    public override create(showAfterCreate: boolean = true): void {
        //if (showAfterCreate) {
        this.show();
        //}
        this.createRecordBtn();

        this.playNext();
    }

    public override destroy(): void {
        super.destroy();
    }
}